import { pgTable, text, serial, integer, boolean, timestamp, json, varchar, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Types for screenplay blocks and character profiles
export enum BlockType {
  DIALOGUE = "dialogue",
  ACTION = "action",
  HEADER = "header"
}

export enum MediaType {
  IMAGE = "image",
  AUDIO = "audio"
}

export enum VideoJobStatus {
  BEING_CREATED = "being created",
  IN_QUEUE = "in queue",
  ERROR = "error"
}

export enum RenderJobStatus {
  PENDING = "pending",
  QUEUED = "queued",
  RENDERING = "rendering",
  PROCESSING = "processing",
  COMPILING = "compiling",
  UPLOADING = "uploading",
  COMPLETE = "complete",
  FAILED = "failed",
  CANCELED = "canceled"
 }

 
export type ClassificationResponse = {
  canProceed: boolean;
  flaggedSections: {
    startBlockId: string;
    endBlockId: string;
    text: string;
    level: "allow" | "warning" | "block";
    categories: string[];
  }[];
};

// export interface Block {
//   id: string;
//   blockOrder: number;
//   type: BlockType;
//   characters: string[];
//   scene: string;
//   originalPageNumber: number;
//   text: string;           // original for narration
//   resolvedText?: string;  // for LLM-driven visual rendering
//   imagePrompt?: string;   // for visual generation
// }

export interface SubscriptionProduct {
  id: string;
  name: string;
  //description: string[];
  monthlyPrice: number;
  annualPrice: number;
  pointsPerMonth: number;
  monthlyPriceId: string;
  annualPriceId: string;
}

export interface ParseResult {
  blocks: ScriptLine[];
  characters: string[];
  speakers: Set<string>;
  pageCount: number;
}

export interface ScriptLine  {
  id: string;
  blockOrder: number;
  type: BlockType;
  characters: string[];
  speaker: string,        // the narrator or name of character speaking
  text: string;           // original for narration  
  imagePrompt?: string;   // for visual generation
  imageUrl?: string[];
  audioUrl?: string;
  duration?: number;
}

export interface CharacterProfile {
  description: string; 
  pronouns: {
    subject: string;    // e.g., he/she/they
    object: string;     // e.g., him/her/them
    possessive: string; // e.g., his/her/their
  };
  isSpeaker: boolean;
}

export const users = pgTable("users", {
  id: uuid("id").defaultRandom().primaryKey(),
  username: text("username").notNull().unique(),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  fullName: text("full_name"),
  usedDemoRender: boolean("used_demo_render").default(false),
  termsAgreedVersion: text("terms_agreed_version"),
  roles: text("roles").array().default([]),
  timezone: text("timezone").default("UTC"),
  stripeCustomerId: text("stripe_customer_id"),
  auth0_id: text("auth0_id").unique(),
  notificationPreferences: json("notification_preferences").$type<{
    renderStarted: boolean;
    renderComplete: boolean;
    payment: boolean;
    renderFailure: boolean;
  }>().default({
    renderStarted: true,
    renderComplete: true,
    payment: true,
    renderFailure: true
  }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  email: true,
  password: true,
  fullName: true,
  termsAgreedVersion: true,
  timezone: true,
  roles: true,
  auth0_id: true,
});

export const screenplays = pgTable("screenplays", {
  id: uuid("id").defaultRandom().primaryKey(),
  userId: uuid("user_id").notNull(),
  title: text("title").notNull(),
  originalFileName: text("original_file_name").notNull(),
  filePath: text("file_path").notNull(),
  status: text("status").default("uploaded").notNull(), // "uploaded" | "processing" | "ready" | "failed" | "rendered"
  estimatedCost: integer("estimated_cost"), // in cents
  estimatedRenderTime: text("estimated_render_time"),
  blocks: json("blocks").$type<ScriptLine[]>().default([]),
  characterDictionary: json("character_dictionary").$type<Record<string, CharacterProfile>>().default({}),
  sceneDictionary: json("scene_dictionary").$type<Record<string, string>>().default({}),
  style: text("style").default("cinematic realism"),
  pageCount: integer("page_count").default(0),
  version: integer("version").default(1),
  notes: text("notes"),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertScreenplaySchema = createInsertSchema(screenplays).pick({
  userId: true,
  title: true,
  originalFileName: true,
  filePath: true,
  status: true,
  style: true,
  notes: true,
});

export const renderJobs = pgTable("render_jobs", {
  id: uuid("id").defaultRandom().primaryKey(),
  userId: uuid("user_id").notNull(),
  screenplayId: uuid("screenplay_id").notNull(),
  title: text("title").notNull(),
  style: text("style").notNull(),
  status: text("status").default("queued"),
  type: text("type").notNull(), // "demo" or "full"
  characterVoices: json("character_voices").$type<Record<string, { id: string, name: string }>>(),
  // Screenplay snapshot - the state of the screenplay at the time of rendering
  screenplaySnapshot: json("screenplay_snapshot").$type<{
    title: string;
    blocks: ScriptLine[];
    characterDictionary: Record<string, CharacterProfile>;
    sceneDictionary: Record<string, string>;
    pageCount: number;
  }>(),
  estimatedCompletionTime: timestamp("estimated_completion_time"),
  completedAt: timestamp("completed_at"),
  outputFilePath: text("output_file_path"),
  posterImagePath: text("poster_image_path"),
  combinedAudioPath: text("combined_audio_path"),
  projectZipPath: text("project_zip_path"),
  shareableLink: text("shareable_link"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertRenderJobSchema = createInsertSchema(renderJobs).pick({
  userId: true,
  screenplayId: true,
  title: true,
  style: true,
  type: true,
  characterVoices: true,
  screenplaySnapshot: true,
});

export const payments = pgTable("payments", {
  id: uuid("id").defaultRandom().primaryKey(),
  userId: uuid("user_id").notNull(),
 // renderJobId: uuid("render_job_id"),
  amount: integer("amount").notNull(), // in cents
  currency: text("currency").default("USD"),
  status: text("status").notNull(),
  paymentMethod: text("payment_method"),
  description: text("description"),
  // stripePaymentIntentId: text("stripe_payment_intent_id"),
  // stripeInvoiceId: text("stripe_invoice_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPaymentSchema = createInsertSchema(payments).pick({
  userId: true,
 // renderJobId: true,
  amount: true,
  currency: true,
  status: true,
  paymentMethod: true,
  description: true,
 // stripePaymentIntentId: true,
});

export const paymentProcessorIds = pgTable("payment_processor_ids", {
  id: uuid("id").defaultRandom().primaryKey(),
  internalId: uuid("internal_id").notNull(), // internal id: userId, paymentId, productId, subscriptionId
  type : text("type").notNull(), //"customer id" | "payment intent" | "invoice id" | "product id" | "subscription id"
  processorName: text("processor_name").notNull(), //"stripe", "square"
  externalId: text("external_id").notNull(), // external id (stripe's)
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPaymentProcessorIdSchema = createInsertSchema(paymentProcessorIds).pick({
  internalId: true,
  type: true,
  processorName: true,
  externalId: true,
});

export const renderMedia = pgTable("render_media", {
  id: uuid("id").defaultRandom().primaryKey(),
  userId: uuid("user_id").notNull(),
  screenplayId: uuid("screenplay_id").notNull(),
  renderJobId: uuid("render_job_id").notNull(),
  blockId: text("block_id").notNull(),
  type: text("type").notNull(), // "audio" or "image"
  url: text("url"),
  duration: integer("duration").default(0),//milliseconds 
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertRenderMediaSchema = createInsertSchema(renderMedia).pick({
  userId: true,
  screenplayId: true,
  renderJobId: true,
  blockId: true,
  type: true,
  url: true,
  duration: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type UserSubscription = typeof usersSubscriptions.$inferSelect;
export type SubscriptionEvent = typeof subscriptionEvents.$inferSelect;
export type Screenplay = typeof screenplays.$inferSelect;
export type InsertScreenplay = z.infer<typeof insertScreenplaySchema>;
export type RenderJob = typeof renderJobs.$inferSelect;
export type InsertRenderJob = z.infer<typeof insertRenderJobSchema>;
export type Payment = typeof payments.$inferSelect;
export type PaymentProcessorId = typeof paymentProcessorIds.$inferSelect;
export type InsertPaymentProcessorId = z.infer<typeof insertPaymentProcessorIdSchema>;
export type InsertPayment = z.infer<typeof insertPaymentSchema>;
export type InsertUserSubscription = z.infer<typeof insertUsersSubscriptionsSchema>;
export type InsertSubscriptionEvent = z.infer<typeof insertSubscriptionEventSchema>;
export const errorLogs = pgTable("error_logs", {
  id: uuid("id").defaultRandom().primaryKey(),
  source: text("source").notNull(),
  errorMessage: text("error_message").notNull(), 
  userId: uuid("user_id"),
  renderJobId: uuid("render_job_id"),
  screenplayId: uuid("screenplay_id"),
  blockId: text("block_id"),
  status: text("status").default("pending").notNull(), // 'pending', 'investigating', 'resolved', 'closed'
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertErrorLogSchema = createInsertSchema(errorLogs).pick({
  source: true,
  errorMessage: true,
  userId: true,
  renderJobId: true,
  screenplayId: true,
  blockId: true,
  status: true,
});

// Points ledger: positive for credit, negative for debit
export const pointsLedger = pgTable("points_ledger", {
  id: uuid("id").defaultRandom().primaryKey(),
  userId: uuid("user_id").notNull(),
  delta: integer("delta").notNull(), // +/- points
  reason: text("reason").notNull(), // e.g. 'render', 'purchase', 'subscription'
  referenceId: text("reference_id"), // payment_id or render_job_id
  comment: text("comment"), // Optional details
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Insert schema (useful for validation or type-safe inserts)
export const insertPointsLedgerSchema = createInsertSchema(pointsLedger).pick({
  userId: true,
  delta: true,
  reason: true,
  referenceId: true, // optional on insert
  comment: true,     // optional on insert
});

// users subscriptions
export const usersSubscriptions = pgTable("users_subscriptions", {
  id: uuid("id").defaultRandom().primaryKey(),
  userId: uuid("user_id").notNull(),
  subscriptionId: uuid("subscription_id").notNull(), // product id for overall subscription (relates to both month and annual)
  priceId: uuid("price_id").notNull(), // monthly or annual price id
  status: text("status"), // active | canceled
  currentPeriodEnd: timestamp("current_period_end"), // when next payment is due
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Insert schema (useful for validation or type-safe inserts)
export const insertUsersSubscriptionsSchema = createInsertSchema(usersSubscriptions).pick({
  id: true,
  userId: true,
  subscriptionId: true,
  priceId: true,
  status: true, 
  currentPeriodEnd: true,
});

export const subscriptionEvents = pgTable("subscription_events", {
  id: uuid("id").defaultRandom().primaryKey(),
  createdAt: timestamp("created_at").defaultNow(),
  userId: uuid("user_id").notNull(),
  eventType: text("event_type").notNull(),            // e.g. 'invoice.paid'
  subscriptionId: text("subscription_id"),            // Stripe subscription ID
  status: text("status"),                             // e.g. 'active', 'canceled'
  currentPeriodStart: timestamp("current_period_start"),
  currentPeriodEnd: timestamp("current_period_end"),
  priceId: text("price_id"),                          // optional price reference
  rawData: json("raw_data").$type<Record<string, any>>(), // full webhook body
});

export const insertSubscriptionEventSchema = createInsertSchema(subscriptionEvents).pick({
  id: true,
  userId: true,
  subscriptionId: true,
  priceId: true,
  status: true,
  currentPeriodStart: true,
  currentPeriodEnd: true,
  eventType: true,
  rawData: true,
});

export const videoJobs = pgTable("video_jobs", {
  id: uuid("id").defaultRandom().primaryKey(),
  renderJobId: uuid("render_job_id").notNull(),
  userId: uuid("user_id").notNull(),
  status: text("status").notNull().$type<"in queue" | "being created" | "error">(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertVideoJobSchema = createInsertSchema(videoJobs).pick({
  renderJobId: true,
  userId: true,
  status: true,
});

export type RenderMedia = typeof renderMedia.$inferSelect;
export type InsertRenderMedia = z.infer<typeof insertRenderMediaSchema>;
export type ErrorLog = typeof errorLogs.$inferSelect;
export type InsertErrorLog = z.infer<typeof insertErrorLogSchema>;
export type PointsLedger = typeof pointsLedger.$inferSelect;
export type InsertPointsLedger = z.infer<typeof insertPointsLedgerSchema>;
export type VideoJob = typeof videoJobs.$inferSelect;
export type InsertVideoJob = z.infer<typeof insertVideoJobSchema>;

export const confirmationCodes = pgTable("confirmation_codes", {
  id: uuid("id").defaultRandom().primaryKey(),
  email: text("email").notNull(),
  code: text("code").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertConfirmationCodeSchema = createInsertSchema(confirmationCodes).pick({
  email: true,
  code: true,
});

export const failedAttempts = pgTable("failed_attempts", {
  id: uuid("id").defaultRandom().primaryKey(),
  email: text("email").notNull(),
  type: text("type").notNull().$type<"login" | "resetPassword">(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertFailedAttemptSchema = createInsertSchema(failedAttempts).pick({
  email: true,
  type: true,
}).extend({
  type: z.enum(["login", "resetPassword"])
});

export type ConfirmationCode = typeof confirmationCodes.$inferSelect;
export type InsertConfirmationCode = z.infer<typeof insertConfirmationCodeSchema>;
export type FailedAttempt = typeof failedAttempts.$inferSelect;
export type InsertFailedAttempt = z.infer<typeof insertFailedAttemptSchema>;
